import { useState } from 'react';
import type { Event } from '@/mocks/admin';
import type { AdminReservation } from '@/mocks/admin';
import StatusBadge from '@/components/base/StatusBadge';

interface Props {
  event: Event;
  reservations: AdminReservation[];
  onClose: () => void;
}

const formatPrice = (n: number) =>
  n === 0 ? 'Gratuit' : `${n.toLocaleString('fr-FR')} FCFA`;

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

const formatDateTime = (d: string) =>
  new Date(d).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long',
    year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

const formatSeats = (seats?: number | null) => {
  if (seats == null) return 'Places non limitées';
  return `${seats.toLocaleString('fr-FR')} place${seats > 1 ? 's' : ''}`;
};

const ADMIN_PROFIT_FEE = 500;
const DEXPAY_PAYIN_RATE = 0.02;
const DEXPAY_PAYOUT_RATE = 0.02;

const getEventPriceBreakdown = (event: Event) => {
  if (event.originalTicketPrice !== undefined) {
    return {
      base: event.originalTicketPrice,
      admin: event.adminProfitFee ?? 0,
      payin: event.dexpayPayinFee ?? 0,
      payout: event.dexpayPayoutFee ?? 0,
      total: event.ticketPrice,
    };
  }

  if (event.ticketPrice <= 0) {
    return { base: 0, admin: 0, payin: 0, payout: 0, total: 0 };
  }

  const subtotal = event.ticketPrice + ADMIN_PROFIT_FEE;
  const payin = Math.ceil(subtotal * DEXPAY_PAYIN_RATE);
  const payout = Math.ceil(event.ticketPrice * DEXPAY_PAYOUT_RATE);

  return {
    base: event.ticketPrice,
    admin: ADMIN_PROFIT_FEE,
    payin,
    payout,
    total: Math.ceil(subtotal + payin + payout),
  };
};

const getTicketTypePriceBreakdown = (ticketType: NonNullable<Event['ticketTypes']>[number]) => {
  if (ticketType.originalPrice !== undefined) {
    return {
      base: ticketType.originalPrice,
      admin: ticketType.adminProfitFee ?? 0,
      payin: ticketType.dexpayPayinFee ?? 0,
      payout: ticketType.dexpayPayoutFee ?? 0,
      total: ticketType.price,
    };
  }

  if (ticketType.price <= 0) {
    return { base: 0, admin: 0, payin: 0, payout: 0, total: 0 };
  }

  const subtotal = ticketType.price + ADMIN_PROFIT_FEE;
  const payin = Math.ceil(subtotal * DEXPAY_PAYIN_RATE);
  const payout = Math.ceil(ticketType.price * DEXPAY_PAYOUT_RATE);

  return {
    base: ticketType.price,
    admin: ADMIN_PROFIT_FEE,
    payin,
    payout,
    total: Math.ceil(subtotal + payin + payout),
  };
};

const RESA_STATUS_CONFIG = {
  confirmed: { label: 'Confirmé', badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  pending:   { label: 'En attente', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400' },
  cancelled: { label: 'Annulé', badge: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400' },
};

const TICKET_STATUS_CONFIG = {
  confirmed: {
    label: 'Confirmé', bg: 'bg-emerald-50', border: 'border-emerald-200',
    text: 'text-emerald-700', dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700',
    icon: 'ri-checkbox-circle-fill',
    bannerMsg: 'Ce ticket est valide et prêt à être scanné à l\'entrée.',
  },
  pending: {
    label: 'En attente', bg: 'bg-amber-50', border: 'border-amber-200',
    text: 'text-amber-700', dot: 'bg-amber-400', badge: 'bg-amber-100 text-amber-700',
    icon: 'ri-time-fill',
    bannerMsg: 'Le paiement est en cours de vérification. Le ticket sera activé sous peu.',
  },
  cancelled: {
    label: 'Annulé', bg: 'bg-gray-50', border: 'border-gray-200',
    text: 'text-gray-500', dot: 'bg-gray-400', badge: 'bg-gray-100 text-gray-500',
    icon: 'ri-close-circle-fill',
    bannerMsg: 'Cette réservation a été annulée et n\'est plus valide.',
  },
};

interface TicketPanelProps {
  reservation: AdminReservation;
  eventTitle: string;
  onBack: () => void;
}

function TicketSubPanel({ reservation, eventTitle, onBack }: TicketPanelProps) {
  const sc = TICKET_STATUS_CONFIG[reservation.status];
  const qrData = `TICKET:${reservation.ticketCode}|EVENT:${reservation.eventId}|BUYER:${reservation.email}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(qrData)}&color=1e2a4a&bgcolor=ffffff&qzone=1`;
  const pricePerTicket = reservation.tickets > 0 ? reservation.total / reservation.tickets : reservation.total;

  return (
    <div className="absolute inset-0 bg-gray-50 z-10 flex flex-col">
      {/* Sub header */}
      <div className="flex-shrink-0 bg-navy px-5 py-4 flex items-center gap-3">
        <button
          onClick={onBack}
          className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg text-white cursor-pointer transition-colors"
        >
          <i className="ri-arrow-left-line text-base"></i>
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-white/60 font-medium">Détail du ticket</p>
          <p className="text-sm font-bold text-white font-mono tracking-wide">{reservation.ticketCode}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {/* Status banner */}
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${sc.bg} ${sc.border}`}>
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sc.dot}`}></div>
          <i className={`${sc.icon} ${sc.text} text-base flex-shrink-0`}></i>
          <p className={`text-xs font-medium ${sc.text} leading-relaxed`}>{sc.bannerMsg}</p>
        </div>

        {/* Ticket card */}
        <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
          {/* Top — event + buyer */}
          <div className="px-5 pt-5 pb-4 bg-gradient-to-br from-navy to-navy-lighter">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold text-white/50 uppercase tracking-widest mb-1">Événement</p>
                <h3 className="text-sm font-bold text-white leading-tight">{eventTitle}</h3>
              </div>
              <span className={`flex-shrink-0 text-xs font-bold px-2.5 py-1 rounded-full ${sc.badge}`}>{sc.label}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-full flex-shrink-0">
                <i className="ri-user-line text-white text-sm"></i>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-white">{reservation.buyer}</p>
                <p className="text-xs text-white/60">{reservation.email}</p>
              </div>
            </div>
          </div>

          {/* Perforated separator */}
          <div className="relative flex items-center">
            <div className="w-5 h-5 rounded-full bg-gray-50 flex-shrink-0 -ml-2.5 border-r border-gray-100"></div>
            <div className="flex-1 border-t-2 border-dashed border-gray-200 mx-1"></div>
            <div className="w-5 h-5 rounded-full bg-gray-50 flex-shrink-0 -mr-2.5 border-l border-gray-100"></div>
          </div>

          {/* QR code */}
          <div className="px-5 py-5 flex flex-col items-center gap-3">
            <div className={`p-3 rounded-2xl border-2 ${reservation.status === 'cancelled' ? 'border-gray-200 opacity-40' : 'border-navy/10'} bg-white relative`}>
              {reservation.status === 'cancelled' && (
                <div className="absolute inset-0 flex items-center justify-center z-10 rounded-xl bg-white/80">
                  <div className="flex flex-col items-center gap-1">
                    <i className="ri-close-circle-fill text-4xl text-gray-400"></i>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Annulé</p>
                  </div>
                </div>
              )}
              <img src={qrUrl} alt={`QR ${reservation.ticketCode}`} className="w-44 h-44" loading="lazy" />
            </div>
            <div className="text-center">
              <p className="font-mono text-sm font-bold text-navy tracking-widest">{reservation.ticketCode}</p>
              <p className="text-xs text-gray-400 mt-0.5">Présenter à l&apos;entrée de l&apos;événement</p>
            </div>
          </div>

          {/* Perforated separator */}
          <div className="relative flex items-center">
            <div className="w-5 h-5 rounded-full bg-gray-50 flex-shrink-0 -ml-2.5 border-r border-gray-100"></div>
            <div className="flex-1 border-t-2 border-dashed border-gray-200 mx-1"></div>
            <div className="w-5 h-5 rounded-full bg-gray-50 flex-shrink-0 -mr-2.5 border-l border-gray-100"></div>
          </div>

          {/* Details grid */}
          <div className="px-5 py-4 grid grid-cols-2 gap-3">
            {[
              { label: 'Tickets', value: `${reservation.tickets} ticket${reservation.tickets > 1 ? 's' : ''}`, icon: 'ri-ticket-line', color: 'text-navy' },
              { label: 'Prix unitaire', value: formatPrice(pricePerTicket), icon: 'ri-price-tag-3-line', color: 'text-navy' },
              { label: 'Total payé', value: formatPrice(reservation.total), icon: 'ri-money-cny-circle-line', color: 'text-emerald-600' },
              { label: 'Téléphone', value: reservation.phone, icon: 'ri-phone-line', color: 'text-navy' },
              { label: 'Adresse', value: reservation.address || 'Non renseigné', icon: 'ri-map-pin-line', color: 'text-navy' },
            ].map((item) => (
              <div key={item.label} className="bg-gray-50 rounded-xl p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <i className={`${item.icon} text-xs text-gray-400`}></i>
                  <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">{item.label}</p>
                </div>
                <p className={`text-sm font-bold ${item.color}`}>{item.value}</p>
              </div>
            ))}
          </div>

          {/* Date footer */}
          <div className="px-5 pb-5">
            <div className="bg-navy/5 rounded-xl px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-navy rounded-lg flex-shrink-0">
                <i className="ri-calendar-check-line text-white text-sm"></i>
              </div>
              <div>
                <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Réservé le</p>
                <p className="text-xs font-semibold text-navy capitalize">{formatDateTime(reservation.date)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-2.5 bg-amber-50 rounded-xl px-4 py-3">
          <i className="ri-information-line text-amber-500 text-base flex-shrink-0 mt-0.5"></i>
          <p className="text-xs text-amber-700 leading-relaxed">
            QR code unique lié à cette réservation. Scanné à l&apos;entrée pour valider l&apos;accès.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 px-5 py-4 bg-white border-t border-gray-100 flex gap-3">
        <button onClick={onBack} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 font-semibold hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors">
          ← Retour
        </button>
        <button onClick={() => window.print()} className="flex items-center justify-center gap-2 px-5 py-2.5 bg-navy text-white rounded-xl text-sm font-semibold hover:bg-navy-lighter cursor-pointer whitespace-nowrap transition-colors">
          <i className="ri-printer-line"></i>Imprimer
        </button>
      </div>
    </div>
  );
}

export default function AdminEventDetailPanel({ event, reservations, onClose }: Props) {
  const [selectedResa, setSelectedResa] = useState<AdminReservation | null>(null);
  const [resaFilter, setResaFilter] = useState<'all' | 'confirmed' | 'pending' | 'cancelled'>('all');

  const totalRevenue = reservations.reduce((sum, r) => sum + (r.status !== 'cancelled' ? r.total : 0), 0);
  const totalTickets = reservations.reduce((sum, r) => sum + (r.status !== 'cancelled' ? r.tickets : 0), 0);
  const confirmedCount = reservations.filter((r) => r.status === 'confirmed').length;
  const fillRate = event.totalSeats > 0 ? Math.round((event.soldSeats / event.totalSeats) * 100) : 0;
  const ticketTypes = event.ticketTypes?.length
    ? event.ticketTypes
    : [{ name: 'Standard', price: event.ticketPrice, seats: event.totalSeats, description: '' }];

  const filteredResa = reservations.filter((r) => resaFilter === 'all' ? true : r.status === resaFilter);

  const filterBtns: { key: typeof resaFilter; label: string; count: number }[] = [
    { key: 'all', label: 'Toutes', count: reservations.length },
    { key: 'confirmed', label: 'Confirmées', count: reservations.filter(r => r.status === 'confirmed').length },
    { key: 'pending', label: 'En attente', count: reservations.filter(r => r.status === 'pending').length },
    { key: 'cancelled', label: 'Annulées', count: reservations.filter(r => r.status === 'cancelled').length },
  ];

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-xl bg-gray-50 z-50 flex flex-col" style={{ boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}>

        {/* Ticket sub-panel overlay */}
        {selectedResa && (
          <TicketSubPanel
            reservation={selectedResa}
            eventTitle={event.title}
            onBack={() => setSelectedResa(null)}
          />
        )}

        {/* Header */}
        <div className="flex-shrink-0 bg-navy px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg">
              <i className="ri-calendar-event-line text-white text-base"></i>
            </div>
            <div>
              <p className="text-xs text-white/60 font-medium">Détail de l'événement</p>
              <p className="text-sm font-bold text-white truncate max-w-[280px]">{event.title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg text-white cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">

          {/* Event image + info */}
          <div className="relative">
            <img src={event.image} alt={event.title} className="w-full h-44 object-cover object-top bg-gray-200" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
              <div>
                <span className="text-xs font-bold text-white/80 uppercase tracking-widest">{event.type}</span>
                <h3 className="text-base font-bold text-white leading-tight mt-0.5">{event.title}</h3>
              </div>
              <StatusBadge status={event.status} />
            </div>
          </div>

          <div className="p-5 space-y-5">
            {/* Stats row */}
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: 'Réservations', value: reservations.length, icon: 'ri-ticket-2-line', color: 'text-navy' },
                { label: 'Confirmées', value: confirmedCount, icon: 'ri-checkbox-circle-line', color: 'text-emerald-600' },
                { label: 'Taux remplissage', value: `${fillRate}%`, icon: 'ri-pie-chart-line', color: 'text-amber-500' },
                { label: 'Revenus', value: `${(totalRevenue / 1000).toFixed(0)}k`, icon: 'ri-money-cny-circle-line', color: 'text-navy' },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-3 text-center">
                  <div className="w-7 h-7 flex items-center justify-center mx-auto mb-1.5 bg-gray-50 rounded-lg">
                    <i className={`${s.icon} text-sm ${s.color}`}></i>
                  </div>
                  <p className={`text-sm font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Event details grid */}
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-3">Informations</h4>
              <div className="grid grid-cols-2 gap-2.5">
                {[
                  { label: 'Date', value: formatDate(event.date), icon: 'ri-calendar-line' },
                  { label: 'Lieu', value: event.location, icon: 'ri-map-pin-line' },
                  { label: 'Prix ticket', value: formatPrice(event.ticketPrice), icon: 'ri-price-tag-3-line' },
                  { label: 'Capacité', value: `${event.soldSeats} / ${event.totalSeats}`, icon: 'ri-group-line' },
                  { label: 'Organisateur', value: event.organizerName, icon: 'ri-user-star-line' },
                  { label: 'Contact', value: event.contact, icon: 'ri-phone-line' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-2.5 bg-gray-50 rounded-lg px-3 py-2.5">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-xs text-gray-400`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wide">{item.label}</p>
                      <p className="text-xs font-semibold text-gray-800 truncate">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 bg-gray-50 rounded-lg px-3 py-2.5">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Description</p>
                <p className="text-xs text-gray-600 leading-relaxed">{event.description}</p>
              </div>
              <div className="mt-3 bg-emerald-50 rounded-lg px-3 py-2.5 border border-emerald-100">
                <p className="text-[10px] text-emerald-700 font-bold uppercase tracking-wide mb-2">Prix avec bénéfice admin et frais Dexpay</p>
                {(() => {
                  const breakdown = getEventPriceBreakdown(event);
                  return (
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <span className="text-gray-500">Prix organisateur</span><span className="font-semibold text-gray-900 text-right">{formatPrice(breakdown.base)}</span>
                      <span className="text-gray-500">Bénéfice admin</span><span className="font-semibold text-gray-900 text-right">{formatPrice(breakdown.admin)}</span>
                      <span className="text-gray-500">Frais payin Dexpay</span><span className="font-semibold text-gray-900 text-right">{formatPrice(breakdown.payin)}</span>
                      <span className="text-gray-500">Frais payout Dexpay</span><span className="font-semibold text-gray-900 text-right">{formatPrice(breakdown.payout)}</span>
                      <span className="font-bold text-emerald-700">Prix approuvé</span><span className="font-bold text-emerald-700 text-right">{formatPrice(breakdown.total)}</span>
                    </div>
                  );
                })()}
              </div>
              {event.rejectionReason && (
                <div className="mt-3 bg-red-50 rounded-lg px-3 py-2.5 border border-red-100">
                  <p className="text-[10px] text-red-500 font-bold uppercase tracking-wide mb-1">Raison du rejet</p>
                  <p className="text-xs text-red-700">{event.rejectionReason}</p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-3">Catégories de tickets</h4>
              <div className="space-y-2.5">
                {ticketTypes.map((ticketType, index) => (
                  <div key={`${ticketType.id ?? ticketType.name}-${index}`} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-navy">{ticketType.name}</p>
                        {ticketType.description ? (
                          <p className="mt-1 text-xs leading-5 text-gray-500">{ticketType.description}</p>
                        ) : null}
                      </div>
                      <span className="rounded-full bg-gold/10 px-3 py-1 text-xs font-bold text-gold-dark">
                        {formatPrice(ticketType.price)}
                      </span>
                    </div>
                    {(() => {
                      const breakdown = getTicketTypePriceBreakdown(ticketType);
                      return (
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <div className="rounded-lg bg-white px-3 py-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Prix organisateur</p>
                            <p className="mt-1 text-xs font-bold text-navy">{formatPrice(breakdown.base)}</p>
                          </div>
                          <div className="rounded-lg bg-white px-3 py-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Commission FSN</p>
                            <p className="mt-1 text-xs font-bold text-emerald-700">{formatPrice(breakdown.admin)}</p>
                          </div>
                          <div className="rounded-lg bg-white px-3 py-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">DexPay payin 2%</p>
                            <p className="mt-1 text-xs font-bold text-blue-700">{formatPrice(breakdown.payin)}</p>
                          </div>
                          <div className="rounded-lg bg-white px-3 py-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">DexPay payout 2%</p>
                            <p className="mt-1 text-xs font-bold text-blue-700">{formatPrice(breakdown.payout)}</p>
                          </div>
                          <div className="rounded-lg bg-white px-3 py-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Places</p>
                            <p className="mt-1 text-xs font-bold text-navy">{formatSeats(ticketType.seats)}</p>
                          </div>
                          <div className="rounded-lg bg-emerald-50 px-3 py-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Prix final</p>
                            <p className="mt-1 text-xs font-bold text-emerald-700">{formatPrice(breakdown.total)}</p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>

            {/* Fill rate bar */}
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-700">Taux de remplissage</span>
                <span className="text-xs font-bold text-navy">{fillRate}% · {totalTickets} tickets vendus</span>
              </div>
              <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${fillRate}%`,
                    background: fillRate >= 80 ? '#22c55e' : fillRate >= 50 ? '#f59e0b' : '#6366f1',
                  }}
                />
              </div>
              <div className="flex justify-between mt-1.5">
                <span className="text-[10px] text-gray-400">0</span>
                <span className="text-[10px] text-gray-400">{event.totalSeats} places</span>
              </div>
            </div>

            {/* Reservations list */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center bg-navy/10 rounded-lg">
                    <i className="ri-ticket-2-line text-navy text-xs"></i>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-gray-900">Réservations</h4>
                    <p className="text-[10px] text-gray-400">{reservations.length} au total</p>
                  </div>
                </div>
                {/* Revenus total */}
                <div className="text-right">
                  <p className="text-xs font-bold text-emerald-600">{formatPrice(totalRevenue)}</p>
                  <p className="text-[10px] text-gray-400">revenus totaux</p>
                </div>
              </div>

              {/* Filter tabs */}
              <div className="flex items-center gap-1 px-3 py-2 bg-gray-50 border-b border-gray-100">
                {filterBtns.map((btn) => (
                  <button
                    key={btn.key}
                    onClick={() => setResaFilter(btn.key)}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap
                      ${resaFilter === btn.key ? 'bg-navy text-white' : 'text-gray-500 hover:bg-gray-200'}`}
                  >
                    {btn.label}
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${resaFilter === btn.key ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'}`}>
                      {btn.count}
                    </span>
                  </button>
                ))}
              </div>

              {/* Reservations rows */}
              {filteredResa.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <i className="ri-inbox-line text-2xl block mb-1"></i>
                  <p className="text-xs">Aucune réservation dans cette catégorie</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {filteredResa.map((resa) => {
                    const sc = RESA_STATUS_CONFIG[resa.status];
                    return (
                      <button
                        key={resa.id}
                        onClick={() => setSelectedResa(resa)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer text-left group"
                      >
                        {/* Avatar */}
                        <div className="w-8 h-8 flex items-center justify-center bg-navy/10 rounded-full flex-shrink-0">
                          <i className="ri-user-line text-navy text-xs"></i>
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-900 truncate">{resa.buyer}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-gray-400 font-mono">{resa.ticketCode}</span>
                            <span className="text-[10px] text-gray-300">·</span>
                            <span className="text-[10px] text-gray-400">{resa.tickets} billet{resa.tickets > 1 ? 's' : ''}</span>
                          </div>
                        </div>
                        {/* Right */}
                        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${sc.badge}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
                            {sc.label}
                          </span>
                          <p className="text-[10px] font-bold text-gray-700">{formatPrice(resa.total)}</p>
                        </div>
                        {/* Arrow */}
                        <div className="w-5 h-5 flex items-center justify-center text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0">
                          <i className="ri-arrow-right-s-line text-sm"></i>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-5 py-4 bg-white border-t border-gray-100">
          <button
            onClick={onClose}
            className="w-full py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 font-semibold hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </>
  );
}
